import React, { Component } from 'react';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import WarningAmber from '@mui/icons-material/WarningAmber';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader } from '@/ui';
import { connect } from 'react-redux';
import { saveAsEntityName, resetModal } from '../../actions';

const initialData = {
  isOpen: false,
  formValidationError: false,
  formErrorMessage: null,
  saveErrorMessage: null,
  saveError: null,
};
const states = {
  open: 'OPEN',
  closed: 'CLOSED',
  saving: 'SAVING',
  updating: 'UPDATING',
  saveError: 'SAVE_ERROR',
  formValidationError: 'FORM_VALIDATION_ERROR',
};
const bodyIconSx = (color) => ({ fontSize: '3rem', color, flex: '0 0 auto' });

class SaveAsEntityModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      newEntityName: '',
      state: states.closed,
      data: initialData,
    };
  }

  componentWillReceiveProps(nextProps, nextContext) {
    // toggle success state if stuck
    const isStuck = this.state.state === states.submitting;
    if (isStuck) {
      this.moveToState(states.closed);
    }

    // if form submission failed due to validation
    const errors = this.props.getErrors();
    if (errors.invalid) {
      this.moveToState(states.formValidationError);
    }

    if (nextProps.saveError && nextProps.saveError.response) {
      this.moveToState(states.saveError);
    }
  }

  moveToState = (state, payload = {}) => {
    const { open, closed, saving, updating, saveError, formValidationError } = states;

    switch (state) {
      case open:
        this.setState((prevState) => ({
          state: open,
          data: { ...prevState.data, isOpen: true, formValidationError: false },
        }));
        break;
      case closed:
        this.props.saveAsEntityName(null);
        setTimeout(() => {
          this.props.resetModal();
        }, 5000);
        this.setState((prevState) => ({
          state: closed,
          data: { ...prevState.data, ...initialData },
        }));
        break;
      case saving:
        const afterSetLocal = async () => {
          if (this.state.newEntityName === '') {
            this.setState((prevState) => ({
              ...prevState,
              data: { ...prevState.data, formErrorMessage: 'Must provide a name' },
            }));
            this.moveToState(open);
          } else {
            await this.props.saveAsEntityName(this.state.newEntityName);
            const isSuccessful = await this.props.onClickSubmit();

            if (isSuccessful) {
              this.moveToState(closed);
              await this.props.saveAsEntityName(null);
            } else {
              this.moveToState(saveError, { saveError: 'Failed to save' });
              await this.props.saveAsEntityName(null);
            }
          }
        };

        this.setState(
          (prevState) => ({
            state: saving,
            data: { ...prevState.data, formErrorMessage: null },
          }),
          afterSetLocal
        );

        break;
      case updating:
        this.setState(
          (prevState) => ({
            newEntityName: payload.newEntityName,
            state: updating,
            data: {
              ...prevState.data,
              formErrorMessage: null,
            },
          }),
          () => {
            this.moveToState(open);
          }
        );
        break;
      case saveError:
        this.setState((prevState) => ({
          state: saveError,
          data: { ...prevState.data, ...payload },
        }));
        break;
      case formValidationError:
        this.setState((prevState) => ({
          state: formValidationError,
          data: { ...prevState.data },
        }));
        break;
      default:
        console.log(`<${state}> is not a valid state for ${this.props.entityType} modal`);
    }
  };

  save = () => {
    this.moveToState(states.saving);
  };

  closeModal = () => {
    this.moveToState(states.closed);
  };

  openModal = () => {
    this.moveToState(states.open);
  };

  updateNewItemName = (event) => {
    this.moveToState(states.updating, { newEntityName: event.target.value });
  };

  toggleFunc = () => {
    const { state } = this.state;

    if (state !== states.saving) {
      return this.moveToState(states.closed);
    }

    return () => {};
  };

  /**
   * Need this to share what an error means to rendering the body and footer in the modal.
   * This differs from this.props.getErrors in that it is used to tell if the button should be shown or not.
   */
  getError = () => this.props.saveError || this.state.data.saveError || null;

  renderErrorBody = () => {
    const errorMessage =
      this.props.saveError && this.props.saveError.response
        ? this.props.saveError.response.data.message
        : this.state.data.saveError
        ? this.state.data.saveError
        : '';

    return (
      <ModalBody>
        <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
          <WarningAmber sx={bodyIconSx('error.main')} />
          <div>
            <Typography sx={{ mb: 2 }}>
              Error copying {`${this.props.entityType}`}. Please close and try again.
            </Typography>
            <Typography sx={{ mb: 1 }}>Message:</Typography>
            <Alert color='danger'>{errorMessage}</Alert>
          </div>
        </Stack>
      </ModalBody>
    );
  };

  renderNormalBody = () => {
    const formError = this.state.data.formErrorMessage;

    return (
      <ModalBody>
        <Stack direction='row' spacing={2} sx={{ alignItems: 'flex-start' }}>
          <WarningAmber sx={bodyIconSx('warning.main')} />
          <div>
            <Typography variant='h6'>{this.props.content && this.props.content.name}</Typography>
            <Typography>
              Any changes made without saving will be <strong>copied</strong> to the new offer but not reflected in the{' '}
              <strong>current</strong> offer until you click save offer.
            </Typography>
          </div>
        </Stack>
        <TextField
          name='newEntityName'
          placeholder={`New ${this.props.entityType}  Name`}
          onChange={this.updateNewItemName}
          value={this.state.newEntityName}
          error={Boolean(formError)}
          helperText={formError || ''}
          sx={{ mt: 2 }}
        />
        {this.props.children}
      </ModalBody>
    );
  };

  renderBody = () => {
    if (this.getError()) {
      return this.renderErrorBody();
    } else {
      return this.renderNormalBody();
    }
  };

  renderErrorFooter = () => {
    return (
      <ModalFooter>
        <Button color='secondary' onClick={this.closeModal}>
          Close
        </Button>
      </ModalFooter>
    );
  };

  renderNormalFooter = () => {
    const { state } = this.state;
    const isSaving = state === states.saving;

    return (
      <ModalFooter>
        <Button color='warning' disabled={isSaving} onClick={this.save}>
          {isSaving ? 'Processing' : 'Copy'}
        </Button>
        <Button color='secondary' disabled={isSaving} onClick={this.closeModal}>
          {isSaving ? 'Processing' : 'Cancel'}
        </Button>
      </ModalFooter>
    );
  };

  renderFooter = () => {
    if (this.getError()) {
      return this.renderErrorFooter();
    } else {
      return this.renderNormalFooter();
    }
  };

  render() {
    const errors = this.props.getErrors();
    const isDisabled = errors.invalid || !this.props.isEditMode;

    return (
      <div>
        <Button
          color='success'
          onClick={this.openModal}
          disabled={isDisabled}
          // sx rather than an inline style: the action bars and the detail header reset
          // float/margins on the buttons they lay out, and an inline style outranks that reset.
          sx={{ float: 'right', ml: 2, mb: 2 }}
          className={this.props.className}
        >
          Copy {`${this.props.entityType}`}
        </Button>
        <Modal
          isOpen={this.state.data.isOpen}
          toggle={this.toggleFunc}
          onClosed={this.closeModal}
          tone={this.getError() ? 'danger' : 'warning'}
        >
          <ModalHeader toggle={this.closeModal}>Copy</ModalHeader>
          {this.renderBody()}
          {this.renderFooter()}
        </Modal>
      </div>
    );
  }
}

const mapState = (state) => {
  const { saveError } = state.landingPage;
  return { saveError };
};

// const mapDispatch = { setNewLandingPageName, resetModal };

export default connect(mapState, { saveAsEntityName, resetModal })(SaveAsEntityModal);
